"use client"
import { useEffect, useMemo, useRef, useState } from "react"
import { AnimatePresence, motion } from "motion/react"
import NumberFlow from "@number-flow/react"
import { useAnimationTuning } from "@/app/playbook/animation/animationTuningContext"
import { resolveCssEasing } from "@/app/playbook/animation/animationTuningDefaults"
import { useLaborEconomicValueSegments } from "../sideways/dithered/useLaborEconomicValueSegments"
import { GroupedSquaresCanvas } from "../sideways/dithered/GroupedSquaresCanvas"
import { useGridMaxHeightWithLegendVisible } from "../shared/useGridMaxHeightWithLegendVisible"
import { useGraphMatchedAiAgentsNoun } from "../shared/useGraphMatchedAiAgentsNoun"
import { MetricTrigger } from "../../hud/metric-details/MetricTrigger"
import {
  LABOR_HOVER_SECTION_STYLE,
  useLaborDotsInverted,
  useLaborLegendPaddingReserved,
  useLaborLegendTreatment,
} from "../../hud/blocks/labor/laborHoverSwap"

// The dot grid above the legend rows. Every dot is a fixed slice of world
// economic value (dollarsPerDot), so the field GROWS with the story — new
// dots wrap into rows added at the bottom, hollow human dots first and the
// FILLED (AI) dots at the end. Rendered by GroupedSquaresCanvas (the same
// engine as the old grouped-squares grid: animated growth, rows never
// overflow maxGridHeightPx — dollarsPerDot is raised internally instead).
// minAiDots keeps the red from disappearing at the scenario start.
export const LABOR_DOT_STRIP = {
  // Dot look matches the original fixed strip (7px, 1.25px hollow stroke);
  // only the growth behavior comes from HUD 91's grid engine.
  dotDiameterPx: 7,
  dotGapPx: 2,
  dollarsPerDot: 1.75e12,
  // Grid height cap without responsiveMaxHeight (mobile HUD, metric panel);
  // with it, this is only the pre-measure/SSR value — the live cap tracks
  // the screen instead.
  maxGridHeightPx: 96,
  maxGridWidthPx: 520,
  hollowStrokeWidthPx: 1.25,
  hollowColor: "#000",
  filledColor: "var(--accent)",
  minAiDots: 1,
}

// Module-level so their identity is stable across renders: the segments hook
// memoizes on colorOverrides identity, and GroupedSquaresCanvas is memo'd with
// a tween-restarting effect keyed on its props — inline literals here would
// restart the dot tweens on every scroll-frame render.
const DOT_COLOR_OVERRIDES = { aiCog: LABOR_DOT_STRIP.filledColor }
const DOT_OUTLINE_BY_KEY = {
  humans: { color: LABOR_DOT_STRIP.hollowColor, widthPx: LABOR_DOT_STRIP.hollowStrokeWidthPx },
}
const MIN_DOT_COUNT_BY_KEY = { aiCog: LABOR_DOT_STRIP.minAiDots }

// Tab 9 "dots-invert" labor hover (see laborHoverSwap.tsx): the policy
// squares' red-fill-invert applied to the dots — the hollow human dots fill
// solid red while the red AI dots turn to red outlines. Module-level (stable
// identities per hover state): the canvas resolves these tween-free, so the
// swap repaints without restarting the dot growth animations.
const DOT_OUTLINE_BY_KEY_INVERTED = {
  aiCog: { color: LABOR_DOT_STRIP.filledColor, widthPx: LABOR_DOT_STRIP.hollowStrokeWidthPx },
}
const DOT_FILL_BY_KEY_INVERTED = { humans: LABOR_DOT_STRIP.filledColor }

// The legend under the strip: "○ 3.5B Human Labor" /
// "● 220K Automated Coders ····· at up to ×150 speed". The AI row's noun
// comes from the capabilities chart's current threshold label (same casing as
// the chart), so it tracks the story date; the AI speed is the FASTEST copies
// run (the inference-specialized fleet — see the labor hover's split for the
// fraction actually running there); everything else is editable here. An
// empty humanSpeedText drops the speed from the human row entirely.
export const LABOR_LEADER_ROWS = {
  humanLabel: "Human Labor",
  humanSpeedText: "×1 speed",
  aiSpeedPrefix: "at up to ×",
  aiSpeedSuffix: " speed",
  numberColorHumans: "#000",
  numberColorAi: "var(--accent)",
  labelFontSize: "8.5px",
  speedFontSize: "8.5px",
  swatchDiameterPx: 7,
  leaderStyle: "dotted" as const,
  leaderColor: "#000",
  // The hairlines framing the rows (below the strip, between rows, and at the
  // bottom). "transparent" drops them (2026-07-09: Joanna wants only the
  // dotted leaders, no framing rules) — the 1px slots stay reserved so
  // nothing shifts if they come back.
  rowSeparatorColor: "transparent",
  // Tightened 2026-07-09 (hairlines dropped, rows scooted together —
  // was 5 / 8 when the rules framed them).
  rowPaddingYPx: 2,
  stripToRowsGapPx: 6,
  // Gap between the two entries' inner pieces in the single-line layout.
  inlineEntryGapClass: "gap-2",
}

type LaborDotStripLeaderRowsProps = {
  year?: number
  // Drops every line in the legend — the framing hairlines AND the dotted
  // leaders. Speeds stay right-aligned via the (now invisible) spacer. HUD 93.
  hideLines?: boolean
  // "rows" (default): two stacked full-width rows with leaders. "inline": both
  // entries share one line — human labor on the left, AI on the right — with
  // no leaders or hairlines. HUD 94.
  legendLayout?: "rows" | "inline"
  // Overrides LABOR_LEADER_ROWS.labelFontSize for the "human labor" /
  // "unreliable agents" label text. HUD 96 passes the count size (11px).
  labelFontSize?: string
  // When true, the fixed maxGridHeightPx cap is replaced by a live screen
  // budget: the dot grid may grow until the legend under it reaches the
  // bottom of the HUD container (whose bottom padding is the screen-edge
  // gap) — or the viewport, outside the HUD — never taller than the screen
  // itself, and never shorter than one dot row. The desktop HUD passes this;
  // the mobile HUD and the metric-details panel keep the fixed cap.
  responsiveMaxHeight?: boolean
}

// Live-tunable timing (see animationTuningContext / the studio's "Labor graph"
// section) for the legend's NumberFlow counts / ×speed and the AI-noun label
// resize — one clock so the whole row reads as a single change. Each legend
// piece reads it directly so nothing has to thread the timing down.
const useLaborLegendTiming = () => {
  const tuning = useAnimationTuning()
  return useMemo(() => {
    const easing = resolveCssEasing(tuning.laborLegendTickerEasing)
    return {
      numberFlow: { duration: tuning.laborLegendTickerMs, easing } as EffectTiming,
      durationMs: tuning.laborLegendTickerMs,
      easing,
    }
  }, [tuning.laborLegendTickerMs, tuning.laborLegendTickerEasing])
}

// Bottom-section labor treatment: one edge-to-edge row of dots (hollow =
// human labor, filled = AI agents, proportional to economic value) over a
// legend comparing the two — stacked rows with dotted leaders and
// right-aligned speed multipliers, or a single side-by-side line. Counts and
// the AI noun/speed are the same live data as
// LaborSidewaysBarsSquaresGrouped's legend.
export const LaborDotStripLeaderRows = ({
  year,
  hideLines = false,
  legendLayout = "rows",
  labelFontSize = LABOR_LEADER_ROWS.labelFontSize,
  responsiveMaxHeight = false,
}: LaborDotStripLeaderRowsProps) => {
  const { segments } = useLaborEconomicValueSegments({
    year,
    combineHuman: true,
    colorOverrides: DOT_COLOR_OVERRIDES,
  })
  const aiNoun = useGraphMatchedAiAgentsNoun(true)
  const legendTiming = useLaborLegendTiming()
  const humans = segments.find((s) => s.key === "humans")
  const ai = segments.find((s) => s.key === "aiCog")

  // Legend labor-hover treatments (see laborHoverSwap.tsx): "red" recolors
  // the line; "chips"/"plate" invert it onto red with white content. The
  // counts carry explicit colors, so inheritance alone can't reach them —
  // they're overridden on the entries below.
  const legendTreatment = useLaborLegendTreatment()
  const legendPaddingReserved = useLaborLegendPaddingReserved()
  const legendRedLit = legendTreatment === "red"
  const legendInverted = legendTreatment === "chips" || legendTreatment === "plate"
  const legendRed = LABOR_HOVER_SECTION_STYLE.legendRed.color
  // Tab 9: dots (and the legend swatches, mirroring them) role-swap while
  // the section is hovered.
  const dotsInverted = useLaborDotsInverted()

  // responsiveMaxHeight: the grid's height budget is the space between the
  // strip's top and the screen bottom (minus the legend), re-measured live.
  // The hook is called unconditionally (rules of hooks); without the prop
  // the fixed cap is used and the measurement result is simply ignored.
  const legendRef = useRef<HTMLDivElement | null>(null)
  const screenBudgetMaxHeight = useGridMaxHeightWithLegendVisible({
    maxHeight: Number.POSITIVE_INFINITY,
    legendPosition: "bottom",
    legendRef,
    legendGapPx: LABOR_LEADER_ROWS.stripToRowsGapPx,
    // "At least one row": a single dot row is all the floor guarantees.
    minHeightPx: LABOR_DOT_STRIP.dotDiameterPx,
    initialHeightPx: LABOR_DOT_STRIP.maxGridHeightPx,
  })
  const gridMaxHeight = responsiveMaxHeight
    ? screenBudgetMaxHeight
    : LABOR_DOT_STRIP.maxGridHeightPx

  // Humans first, AI last, so the filled dots sit at the grid's end and new
  // AI dots append at the bottom as the story advances. Memoized so the
  // canvas's segments-keyed tween effect only refires when the data changes.
  const gridSegments = useMemo(
    () => (humans && ai ? [humans, ai] : null),
    [humans, ai],
  )
  if (!gridSegments || !humans || !ai) return null

  const humanEntry: LegendEntry = {
    swatch: dotsInverted ? "filled" : "hollow",
    count: humans.count,
    numberColor: legendInverted
      ? "#fff"
      : legendRedLit ? legendRed : LABOR_LEADER_ROWS.numberColorHumans,
    label: LABOR_LEADER_ROWS.humanLabel,
    speed: LABOR_LEADER_ROWS.humanSpeedText ? <span>{LABOR_LEADER_ROWS.humanSpeedText}</span> : null,
  }
  const aiEntry: LegendEntry = {
    swatch: dotsInverted ? "hollow" : "filled",
    swatchBorderColor: dotsInverted ? LABOR_DOT_STRIP.filledColor : undefined,
    count: ai.count,
    numberColor: legendInverted ? "#fff" : LABOR_LEADER_ROWS.numberColorAi,
    label: aiNoun ?? ai.unit,
    speed: (
      // 2 significant digits: the multiplier is a forecast, and "×1,937"
      // implies precision the model doesn't have.
      <NumberFlow
        value={ai.speedUpToMultiplier ?? ai.speedMultiplier ?? 1}
        format={{ maximumSignificantDigits: 2 }}
        prefix={LABOR_LEADER_ROWS.aiSpeedPrefix}
        suffix={LABOR_LEADER_ROWS.aiSpeedSuffix} transformTiming={legendTiming.numberFlow} spinTiming={legendTiming.numberFlow} />
    ),
  }

  return (
    <MetricTrigger id="labor" className="w-full font-mono text-[11px] leading-tight text-black">
      <GroupedSquaresCanvas
        segments={gridSegments}
        dollarsPerSquareInitial={LABOR_DOT_STRIP.dollarsPerDot}
        startSquareSize={LABOR_DOT_STRIP.dotDiameterPx}
        minSquareSize={LABOR_DOT_STRIP.dotDiameterPx}
        squareGap={LABOR_DOT_STRIP.dotGapPx}
        maxWidth={LABOR_DOT_STRIP.maxGridWidthPx}
        maxHeight={gridMaxHeight}
        squareShape="dot"
        squareOutlineByKey={dotsInverted ? DOT_OUTLINE_BY_KEY_INVERTED : DOT_OUTLINE_BY_KEY}
        squareFillColorByKey={dotsInverted ? DOT_FILL_BY_KEY_INVERTED : undefined}
        minSquareCountByKey={MIN_DOT_COUNT_BY_KEY}
        justifyRows
      />
      {legendLayout === "inline" ? (
        // The chip/plate paddings are CONSTANT on their tabs (side padding
        // cancelled by negative margins so the legend's outer edges stay
        // flush); only background/text colors toggle on hover — the line
        // never shifts.
        <div
          ref={legendRef}
          className="flex items-baseline justify-between"
          style={{
            marginTop: LABOR_LEADER_ROWS.stripToRowsGapPx,
            ...(legendPaddingReserved === "plate" && {
              padding: `${LABOR_HOVER_SECTION_STYLE.legendPlate.paddingYPx}px ${LABOR_HOVER_SECTION_STYLE.legendPlate.paddingXPx}px`,
              marginLeft: -LABOR_HOVER_SECTION_STYLE.legendPlate.paddingXPx,
              marginRight: -LABOR_HOVER_SECTION_STYLE.legendPlate.paddingXPx,
              borderRadius: LABOR_HOVER_SECTION_STYLE.legendPlate.borderRadiusPx,
              ...(legendTreatment === "plate" && {
                background: LABOR_HOVER_SECTION_STYLE.legendPlate.background,
                color: LABOR_HOVER_SECTION_STYLE.legendPlate.textColor,
              }),
            }),
          }}
        >
          {[
            { entry: humanEntry, edge: "left" as const },
            { entry: aiEntry, edge: "right" as const },
          ].map(({ entry, edge }) => (
            <span
              key={edge}
              style={
                legendPaddingReserved === "chips"
                  ? {
                      padding: `${LABOR_HOVER_SECTION_STYLE.legendChips.paddingYPx}px ${LABOR_HOVER_SECTION_STYLE.legendChips.paddingXPx}px`,
                      ...(edge === "left"
                        ? { marginLeft: -LABOR_HOVER_SECTION_STYLE.legendChips.paddingXPx }
                        : { marginRight: -LABOR_HOVER_SECTION_STYLE.legendChips.paddingXPx }),
                      borderRadius: LABOR_HOVER_SECTION_STYLE.legendChips.borderRadiusPx,
                      ...(legendTreatment === "chips" && {
                        background: LABOR_HOVER_SECTION_STYLE.legendChips.background,
                        color: LABOR_HOVER_SECTION_STYLE.legendChips.textColor,
                      }),
                    }
                  : undefined
              }
            >
              <InlineEntry
                entry={entry}
                labelFontSize={labelFontSize}
                invertSwatch={legendInverted}
              />
            </span>
          ))}
        </div>
      ) : (
        <div
          ref={legendRef}
          style={{
            marginTop: LABOR_LEADER_ROWS.stripToRowsGapPx,
            borderTop: hideLines
              ? undefined
              : `1px solid ${LABOR_LEADER_ROWS.rowSeparatorColor}`,
          }}
        >
          <LeaderRow entry={humanEntry} hideLines={hideLines} labelFontSize={labelFontSize} />
          <LeaderRow entry={aiEntry} hideLines={hideLines} labelFontSize={labelFontSize} />
        </div>
      )}
    </MetricTrigger>
  )
}

type LegendEntry = {
  swatch: "hollow" | "filled"
  // Overrides the hollow swatch's ring color (e.g. red while the dots-invert
  // labor hover has role-swapped the AI dots to outlines).
  swatchBorderColor?: string
  count: number
  numberColor: string
  label: string
  speed: React.ReactNode
}

// The AI noun ("Unreliable Agents" → "Reliable Agents" → "Superhuman AI
// Researchers") swaps as the story crosses capability thresholds. Rendered
// plainly it jumps to the new text's width, snapping the leader / speed beside
// it; here the width animates (measured, so the text never scale-distorts) and
// the text crossfades — timed to the count/speed NumberFlow next to it (the
// labor legend timing) so the whole row reads as one change.
const ResizingLabel = ({ text, fontSize }: { text: string; fontSize: string }) => {
  const measureRef = useRef<HTMLSpanElement>(null)
  const [width, setWidth] = useState<number>()
  const timing = useLaborLegendTiming()
  useEffect(() => {
    const measured = measureRef.current?.offsetWidth
    if (measured != null) setWidth(measured)
  }, [text, fontSize])
  return (
    <span
      className="relative inline-block align-baseline whitespace-nowrap"
      style={{
        fontSize,
        width,
        transition: `width ${timing.durationMs}ms ${timing.easing}`,
      }}
    >
      {/* In-flow but invisible: fixes the label's baseline + height and the
          natural target width the outer animates toward. */}
      <span ref={measureRef} aria-hidden className="invisible">{text}</span>
      {/* Visible layers, clipped to the animating width so a longer label
          reveals (instead of overhanging the leader) as the width grows. */}
      <span className="absolute inset-0 overflow-hidden">
        <AnimatePresence initial={false}>
          <motion.span
            key={text}
            className="absolute left-0 top-0 whitespace-nowrap"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: Math.min(timing.durationMs, 280) / 1000, ease: "easeInOut" }}
          >
            {text}
          </motion.span>
        </AnimatePresence>
      </span>
    </span>
  )
}

// The speed never leaves the row: it's shrink-proof and the row's last
// item, so its right edge IS the column's right edge; when the column is
// too narrow for everything (mobile), the LABEL shrinks and clips inside
// its own masked resizer instead of pushing the speed out (2026-07-09:
// "×41 speed" was getting cut off at the column edge on mobile).
const LeaderRow = ({ entry, hideLines, labelFontSize }: { entry: LegendEntry; hideLines: boolean; labelFontSize: string }) => (
  <div
    className="flex items-baseline gap-2"
    style={{
      paddingTop: LABOR_LEADER_ROWS.rowPaddingYPx,
      paddingBottom: LABOR_LEADER_ROWS.rowPaddingYPx,
      borderBottom: hideLines
        ? undefined
        : `1px solid ${LABOR_LEADER_ROWS.rowSeparatorColor}`,
    }}
  >
    <LegendSwatch swatch={entry.swatch} />
    <LegendCount count={entry.count} numberColor={entry.numberColor} />
    <span className="min-w-0 overflow-hidden">
      <ResizingLabel text={entry.label} fontSize={labelFontSize} />
    </span>
    <span
      aria-hidden
      className="flex-1"
      style={{
        alignSelf: "center",
        height: 0,
        borderBottom: hideLines
          ? undefined
          : `1px ${LABOR_LEADER_ROWS.leaderStyle} ${LABOR_LEADER_ROWS.leaderColor}`,
      }}
    />
    <span
      className="whitespace-nowrap text-right"
      style={{ fontSize: LABOR_LEADER_ROWS.speedFontSize, flexShrink: 0 }}
    >
      {entry.speed}
    </span>
  </div>
)

// One legend entry on the shared single line: "○ 3.5B human labor ×1 speed".
// invertSwatch: white-on-red rendering while a legend-inversion labor hover
// (chips/plate — see laborHoverSwap.tsx) is lit.
const InlineEntry = ({ entry, labelFontSize, invertSwatch = false }: { entry: LegendEntry; labelFontSize: string; invertSwatch?: boolean }) => (
  <div className={`flex min-w-0 items-baseline ${LABOR_LEADER_ROWS.inlineEntryGapClass}`}>
    <LegendSwatch swatch={entry.swatch} invert={invertSwatch} borderColor={entry.swatchBorderColor} />
    <LegendCount count={entry.count} numberColor={entry.numberColor} />
    <span className="min-w-0 overflow-hidden">
      <ResizingLabel text={entry.label} fontSize={labelFontSize} />
    </span>
    <span
      className="whitespace-nowrap"
      style={{ fontSize: LABOR_LEADER_ROWS.speedFontSize, flexShrink: 0 }}
    >
      {entry.speed}
    </span>
  </div>
)

const LegendSwatch = ({ swatch, invert = false, borderColor }: { swatch: "hollow" | "filled"; invert?: boolean; borderColor?: string }) => (
  <span
    aria-hidden
    style={{
      alignSelf: "center",
      width: LABOR_LEADER_ROWS.swatchDiameterPx,
      height: LABOR_LEADER_ROWS.swatchDiameterPx,
      borderRadius: "50%",
      boxSizing: "border-box",
      flexShrink: 0,
      backgroundColor:
        swatch === "filled"
          ? invert ? "#fff" : LABOR_DOT_STRIP.filledColor
          : "transparent",
      border:
        swatch === "hollow"
          ? `${LABOR_DOT_STRIP.hollowStrokeWidthPx}px solid ${invert ? "#fff" : borderColor ?? LABOR_DOT_STRIP.hollowColor}`
          : undefined,
    }}
  />
)

const LegendCount = ({ count, numberColor }: { count: number; numberColor: string }) => {
  const timing = useLaborLegendTiming()
  return (
    <span className="font-bold" style={{ color: numberColor }}>
      <NumberFlow
        value={count}
        format={{ notation: "compact", minimumSignificantDigits: 2, maximumSignificantDigits: 2 }} transformTiming={timing.numberFlow} spinTiming={timing.numberFlow} />
    </span>
  )
}
